import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  Speed,
  Analytics,
  Refresh,
} from '@mui/icons-material';
import { useBrandColors } from '../hooks/useBrandColors';

interface TokenStats {
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  estimatedCost: number;
  requestCount: number;
  averageTokensPerRequest: number;
  timeRange: string;
}

interface TokenUsageProps {
  timeRange?: string;
  onTimeRangeChange?: (timeRange: string) => void;
}

const TokenUsage: React.FC<TokenUsageProps> = ({ 
  timeRange = '7d', 
  onTimeRangeChange 
}) => {
  const brandColors = useBrandColors();
  const [tokenStats, setTokenStats] = useState<TokenStats>({
    totalTokens: 0,
    promptTokens: 0,
    completionTokens: 0,
    estimatedCost: 0,
    requestCount: 0,
    averageTokensPerRequest: 0,
    timeRange: '7d'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTokenStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Loading token stats for timeRange:', timeRange);
      const stats = await apiService.getTokenStats(timeRange);
      console.log('✅ Token stats loaded:', stats);
      setTokenStats(stats);
    } catch (error) {
      console.error('❌ Failed to load token stats:', error);
      setError('Failed to load token statistics');
      // Fallback to zero stats
      setTokenStats({
        totalTokens: 0,
        promptTokens: 0,
        completionTokens: 0,
        estimatedCost: 0,
        requestCount: 0,
        averageTokensPerRequest: 0,
        timeRange
      });
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Load real token data from API
  useEffect(() => {
    loadTokenStats();
  }, [loadTokenStats]);

  const styles = {
    container: {
      padding: '0'
    },
    card: {
      marginBottom: '0',
      boxShadow: 'none',
      borderRadius: '0'
    },
    statGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    statCard: {
      padding: '16px',
      borderRadius: '8px',
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef'
    },
    statHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px'
    },
    statValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: brandColors.primary,
      marginBottom: '4px'
    },
    statLabel: {
      fontSize: '12px',
      color: '#666',
      fontWeight: '500'
    },
    progressSection: {
      marginTop: '16px'
    },
    progressLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    }
  };

  return (
    <div style={styles.container}>
      <Card sx={styles.card}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Analytics />
              OpenAI Token Usage
              {loading && <span style={{ color: brandColors.primary, fontSize: '14px' }}>⏳ Loading...</span>}
            </Typography>
            <Button
              size="small"
              onClick={loadTokenStats}
              disabled={loading}
              startIcon={<Refresh />}
              sx={{ minWidth: 'auto' }}
            >
              Refresh
            </Button>
          </Box>

          <div style={styles.statGrid}>
            <div style={styles.statCard}>
              <div style={styles.statHeader}>
                <Speed sx={{ fontSize: '20px', color: brandColors.primary }} />
                <Typography variant="body2" sx={styles.statLabel}>Total Tokens</Typography>
              </div>
              <div style={styles.statValue}>{tokenStats.totalTokens.toLocaleString()}</div>
              <Typography variant="caption" color="text.secondary">
                Across {tokenStats.requestCount} requests
              </Typography>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statHeader}>
                <AttachMoney sx={{ fontSize: '20px', color: brandColors.secondary }} />
                <Typography variant="body2" sx={styles.statLabel}>Estimated Cost</Typography>
              </div>
              <div style={styles.statValue}>${tokenStats.estimatedCost.toFixed(2)}</div>
              <Typography variant="caption" color="text.secondary">
                USD (GPT-4o Vision)
              </Typography>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statHeader}>
                <TrendingUp sx={{ fontSize: '20px', color: brandColors.accent }} />
                <Typography variant="body2" sx={styles.statLabel}>Avg per Request</Typography>
              </div>
              <div style={styles.statValue}>{tokenStats.averageTokensPerRequest}</div>
              <Typography variant="caption" color="text.secondary">
                tokens per screenshot
              </Typography>
            </div>

            <div style={styles.statCard}>
              <div style={styles.statHeader}>
                <Analytics sx={{ fontSize: '20px', color: brandColors.primary }} />
                <Typography variant="body2" sx={styles.statLabel}>Requests</Typography>
              </div>
              <div style={styles.statValue}>{tokenStats.requestCount}</div>
              <Typography variant="caption" color="text.secondary">
                screenshots analyzed
              </Typography>
            </div>
          </div>

          <div style={styles.progressSection}>
            <div style={styles.progressLabel}>
              <Typography variant="body2" fontWeight="500">Token Distribution</Typography>
              <Typography variant="caption">
                {tokenStats.totalTokens > 0 ? Math.round((tokenStats.promptTokens / tokenStats.totalTokens) * 100) : 0}% prompt / {tokenStats.totalTokens > 0 ? Math.round((tokenStats.completionTokens / tokenStats.totalTokens) * 100) : 0}% completion
              </Typography>
            </div>
            <LinearProgress
              variant="determinate"
              value={tokenStats.totalTokens > 0 ? (tokenStats.promptTokens / tokenStats.totalTokens) * 100 : 0}
              sx={{
                height: '8px',
                borderRadius: '4px',
                backgroundColor: '#f0f0f0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: brandColors.accent
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption">
                Prompt: {tokenStats.promptTokens.toLocaleString()}
              </Typography>
              <Typography variant="caption">
                Completion: {tokenStats.completionTokens.toLocaleString()}
              </Typography>
            </Box>
          </div>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label="GPT-4o Vision" 
              color="primary" 
              size="small" 
            />
            <Chip 
              label="Low Detail Mode" 
              color="success" 
              size="small" 
            />
            <Chip 
              label="~324 tokens avg" 
              color="info" 
              size="small" 
            />
            <Chip 
              label="$0.01/1K tokens" 
              color="warning" 
              size="small" 
            />
          </Box>

          {error && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#ffebee', borderRadius: '4px', border: '1px solid #ffcdd2' }}>
              <Typography variant="body2" sx={{ color: '#c62828' }}>
                ⚠️ {error}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TokenUsage;
